import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

export type RoundQualifierBrief = {
  fromRound: number;
  minScore: number;
};

export type EventDivisionCriterion = {
  id: string;
  divisionId: string;
  judgeRole: "judge1" | "judge2" | "judge3";
  name: string;
  maxScore: number;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
};

export type EventDivisionRow = {
  id: string;
  eventId: string;
  name: string;
  slug: string | null;
  sortOrder: number;
  roundCount: number;
  qualifiers: RoundQualifierBrief[];
  extraCriteria: EventDivisionCriterion[];
  createdAt: string;
  updatedAt: string;
};

export type Team = {
  id: string;
  divisionId: string;
  orderNumber: number;
  name: string;
  qualifiedUpToRound: number;
  createdAt: string;
  updatedAt: string;
};

export type CompetitionSettings = {
  isOpen: boolean;
  resultsShown: boolean;
};

export type PublishedEventPick = {
  id: string;
  name: string;
  slug: string;
};

export type TeamResult = {
  id: string;
  divisionId: string;
  orderNumber: number;
  name: string;
  judge1: {
    strengthDifficulty: number;
    timingControl: number;
    unitySynchronicity: number;
    total: number;
    submitted: boolean;
  };
  judge2: {
    charismaConfidence: number;
    facialExpression: number;
    stageAwareness: number;
    total: number;
    submitted: boolean;
  };
  judge3: {
    conceptOriginality: number;
    costumeVisual: number;
    choreographyQuality: number;
    total: number;
    submitted: boolean;
  };
  extraCriteria: {
    criterionId: string;
    judgeRole: string;
    name: string;
    value: number;
    maxScore: number;
  }[];
  extraTotal: number;
  maxTotal: number;
  grandTotal: number;
  rank: number;
};

export type DivisionRoundResults = {
  roundIndex: number;
  teams: TeamResult[];
};

export type DivisionResultsSection = {
  divisionId: string;
  divisionName: string;
  sortOrder: number;
  roundCount: number;
  rounds: DivisionRoundResults[];
};

export type EventResultsPayload = {
  divisions: DivisionResultsSection[];
};

const base = `${config.apiUrl}`;

export const competitionApi = {
  async listDivisions(eventId: string): Promise<EventDivisionRow[]> {
    return fetcher.get<EventDivisionRow[]>(
      `${base}/events/${eventId}/divisions`,
    );
  },
  async createDivision(
    eventId: string,
    input: { name: string; slug?: string | null; sortOrder?: number },
  ): Promise<EventDivisionRow> {
    return fetcher.post<EventDivisionRow>(
      `${base}/events/${eventId}/divisions`,
      input,
    );
  },
  async updateDivision(
    eventId: string,
    divisionId: string,
    input: { name?: string; slug?: string | null; sortOrder?: number },
  ): Promise<EventDivisionRow> {
    return fetcher.patch<EventDivisionRow>(
      `${base}/events/${eventId}/divisions/${divisionId}`,
      input,
    );
  },
  async patchDivisionRoundConfig(
    eventId: string,
    divisionId: string,
    input: {
      roundCount: number;
      qualifiers: { fromRound: number; minScore: number }[];
    },
  ): Promise<EventDivisionRow> {
    return fetcher.patch<EventDivisionRow>(
      `${base}/events/${eventId}/divisions/${divisionId}/round-config`,
      input,
    );
  },
  async createDivisionCriterion(
    eventId: string,
    divisionId: string,
    input: {
      judgeRole: "judge1" | "judge2" | "judge3";
      name: string;
      maxScore: number;
      sortOrder?: number;
    },
  ): Promise<EventDivisionCriterion> {
    return fetcher.post<EventDivisionCriterion>(
      `${base}/events/${eventId}/divisions/${divisionId}/criteria`,
      input,
    );
  },
  async updateDivisionCriterion(
    eventId: string,
    divisionId: string,
    criterionId: string,
    input: {
      judgeRole?: "judge1" | "judge2" | "judge3";
      name?: string;
      maxScore?: number;
      sortOrder?: number;
    },
  ): Promise<EventDivisionCriterion> {
    return fetcher.patch<EventDivisionCriterion>(
      `${base}/events/${eventId}/divisions/${divisionId}/criteria/${criterionId}`,
      input,
    );
  },
  async deleteDivisionCriterion(
    eventId: string,
    divisionId: string,
    criterionId: string,
  ): Promise<EventDivisionCriterion> {
    return fetcher.delete<EventDivisionCriterion>(
      `${base}/events/${eventId}/divisions/${divisionId}/criteria/${criterionId}`,
    );
  },
  async closeDivisionRound(
    eventId: string,
    divisionId: string,
    finishedRound: number,
  ): Promise<{ advancedCount: number }> {
    return fetcher.post<{ advancedCount: number }>(
      `${base}/events/${eventId}/divisions/${divisionId}/close-round`,
      { finishedRound },
    );
  },
  async deleteDivision(
    eventId: string,
    divisionId: string,
  ): Promise<EventDivisionRow> {
    return fetcher.delete<EventDivisionRow>(
      `${base}/events/${eventId}/divisions/${divisionId}`,
    );
  },

  async listTeams(
    eventId: string,
    divisionId?: string,
    roundIndex?: number,
  ): Promise<Team[]> {
    const params = new URLSearchParams();
    if (divisionId != null && divisionId !== "") {
      params.set("divisionId", divisionId);
    }
    if (roundIndex != null && roundIndex >= 1) {
      params.set("roundIndex", String(roundIndex));
    }
    const qs = params.toString();
    return fetcher.get<Team[]>(
      `${base}/events/${eventId}/teams${qs ? `?${qs}` : ""}`,
    );
  },
  async createTeam(
    eventId: string,
    input: {
      divisionId: string;
      name: string;
      orderNumber: number;
    },
  ): Promise<Team> {
    return fetcher.post<Team>(`${base}/events/${eventId}/teams`, input);
  },
  async updateTeam(
    eventId: string,
    id: string,
    input: { divisionId?: string; name?: string; orderNumber?: number },
  ): Promise<Team> {
    return fetcher.patch<Team>(`${base}/events/${eventId}/teams/${id}`, input);
  },
  async deleteTeam(eventId: string, id: string): Promise<Team> {
    return fetcher.delete<Team>(`${base}/events/${eventId}/teams/${id}`);
  },

  // Settings (scoped by eventId)
  async getSettings(eventId: string): Promise<CompetitionSettings> {
    return fetcher.get<CompetitionSettings>(
      `${base}/events/${eventId}/settings`,
    );
  },
  async toggleScoring(eventId: string): Promise<CompetitionSettings> {
    return fetcher.patch<CompetitionSettings>(
      `${base}/events/${eventId}/toggle-scoring`,
    );
  },
  async publishResults(eventId: string): Promise<CompetitionSettings> {
    return fetcher.post<CompetitionSettings>(
      `${base}/events/${eventId}/publish`,
    );
  },
  async reopenScoring(eventId: string): Promise<CompetitionSettings> {
    return fetcher.post<CompetitionSettings>(
      `${base}/events/${eventId}/reopen-scoring`,
    );
  },

  async getResults(eventId: string): Promise<EventResultsPayload> {
    return fetcher.get<EventResultsPayload>(
      `${base}/events/${eventId}/results`,
    );
  },

  async listPublishedEventCatalog(): Promise<PublishedEventPick[]> {
    return fetcher.get<PublishedEventPick[]>(
      `${base}/events/published-visible`,
    );
  },
};
